import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREA_LABELS = {
  auslaenderrecht: 'Ausländerrecht',
  strafrecht: 'Strafrecht',
  steuerberatung: 'Steuerberatung',
  steuerrecht: 'Steuerrecht',
  gesellschaftsrecht: 'Gesellschaftsrecht',
  verwaltungsrecht: 'Verwaltungsrecht',
  mietrecht: 'Mietrecht',
  verkehrsrecht: 'Verkehrsrecht',
};

const FALLBACK_IMAGES = {
  auslaenderrecht: 'https://images.unsplash.com/photo-1591802502430-94be3a5dba43?auto=format&fit=crop&w=1200&q=70',
  strafrecht: 'https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=1200&q=70',
  steuerberatung: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=70',
  steuerrecht: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&w=1200&q=70',
  gesellschaftsrecht: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=70',
  verwaltungsrecht: 'https://images.unsplash.com/photo-1577415124269-fc1140a69e91?auto=format&fit=crop&w=1200&q=70',
  mietrecht: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=70',
  verkehrsrecht: 'https://images.unsplash.com/photo-1574116978193-30bf17ac1fbd?auto=format&fit=crop&w=1200&q=70',
};

const formatDate = (iso, locale) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
};

const NewsTeaser = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/news`, { params: { limit: 3 } })
      .then((res) => setItems(res.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section id="aktuelles-teaser" className="bg-white py-20 lg:py-24 border-t border-gray-100" data-testid="news-teaser-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('newsTeaser.eyebrow', 'AKTUELLES')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">
              {t('newsTeaser.headline', 'Neueste Urteile & Nachrichten')}
            </h2>
          </div>
          <Link
            to="/aktuelles"
            data-testid="news-teaser-all-link"
            className="inline-flex items-center gap-2 text-[#1B3A5C] text-sm tracking-[0.15em] uppercase font-medium hover:text-[#b8893d] transition-colors"
          >
            {t('newsTeaser.all', 'Alle Beiträge')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((n) => (
            <Link
              key={n.id}
              to={`/aktuelles?rechtsgebiet=${n.rechtsgebiet}`}
              data-testid={`news-teaser-card-${n.id}`}
              className="group flex flex-col bg-[#f5f8fa] border-b-2 border-[#b8893d] rounded-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              <img
                src={n.image_url || FALLBACK_IMAGES[n.rechtsgebiet]}
                alt={n.headline}
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const fb = FALLBACK_IMAGES[n.rechtsgebiet];
                  if (fb && e.currentTarget.src !== fb) e.currentTarget.src = fb;
                  else e.currentTarget.style.display = 'none';
                }}
                className="w-full aspect-[16/9] object-cover"
              />
              <div className="p-6 flex flex-col flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] tracking-[0.15em] mb-3">
                  <span className="text-[#b8893d] font-semibold uppercase">{AREA_LABELS[n.rechtsgebiet] || n.rechtsgebiet}</span>
                  <span className="text-[#34536b]/60 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {formatDate(n.published_at, i18n.language)}
                  </span>
                </div>
                <h3 className="font-serif text-lg text-[#1B3A5C] leading-snug mb-3 group-hover:text-[#b8893d] transition-colors">
                  {n.headline}
                </h3>
                <p className="text-[#34536b] text-sm leading-relaxed line-clamp-3">{n.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsTeaser;
